import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";
import {
  FaPhoneAlt,
  FaUser,
  FaStar,
  FaTimes,
  FaMinus,
  FaRegCalendarAlt,
  FaChevronDown,
} from "react-icons/fa";
import SummaryApi from "../common";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip);

const COLORS = {
  interested: "#1f9d6b",
  mostInterested: "#6d3fd8",
  notInterested: "#ef3b3b",
  notAnswered: "#f6a83a",
  callBack: "#1c8ae4"
};

const STATUS = [
  { key: "interested", label: "Interested" },
  { key: "mostInterested", label: "Most Interested" },
  { key: "notInterested", label: "Not Interested" },
  { key: "notAnswered", label: "Not Answered" },
  { key: "callBack", label: "Call Back" },
];

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

const pct = (n, total) => (total ? ((n / total) * 100).toFixed(1) : "0.0");

const EMPTY_ROW = {
  total: 0,
  interested: 0,
  mostInterested: 0,
  notInterested: 0,
  notAnswered: 0,
  callBack: 0
};

const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart, _args, opts) {
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const { x, y } = meta.data[0];
    const { ctx } = chart;
    ctx.save();
    ctx.textAlign = "center";
    ctx.fillStyle = "#111827";
    ctx.font = "700 24px system-ui, sans-serif";
    ctx.fillText(opts.value, x, y + 2);
    ctx.fillStyle = "#6b7280";
    ctx.font = "500 13px system-ui, sans-serif";
    ctx.fillText("Total Calls", x, y + 22);
    ctx.restore();
  },
};

const slicePercentPlugin = {
  id: "slicePercent",
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    const data = chart.data.datasets[0].data;
    const total = data.reduce((a, b) => a + b, 0);
    if (!total) return;
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = "#fff";
    ctx.font = "700 12px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    meta.data.forEach((arc, i) => {
      if (!data[i]) return;
      const angle = (arc.startAngle + arc.endAngle) / 2;
      const r = (arc.innerRadius + arc.outerRadius) / 2;
      ctx.fillText(
        `${pct(data[i], total)}%`,
        arc.x + Math.cos(angle) * r,
        arc.y + Math.sin(angle) * r
      );
    });
    ctx.restore();
  },
};

const barValuePlugin = {
  id: "barValues",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = "#111827";
    ctx.font = "700 11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    chart.data.datasets.forEach((ds, di) => {
      chart.getDatasetMeta(di).data.forEach((bar, i) => {
        ctx.fillText(ds.data[i], bar.x, bar.y - 4);
      });
    });
    ctx.restore();
  },
};

const StatCard = ({ title, value, note, icon: Icon, iconBg, iconColor, cardBg, border }) => (
  <div
    className="rounded-xl p-5 flex gap-4 items-start"
    style={{ background: cardBg, border: `1px solid ${border}` }}
  >
    <div
      className="w-14 h-14 rounded-full flex items-center justify-center shrink-0"
      style={{ background: iconBg }}
    >
      <span
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-base"
        style={{ background: iconColor }}
      >
        <Icon />
      </span>
    </div>
    <div>
      <p className="text-sm text-gray-700">{title}</p>
      <p className="text-3xl font-semibold text-gray-900 leading-tight">
        {value?.toLocaleString()}
      </p>
      <p className="text-xs text-gray-500 mt-1">{note}</p>
    </div>
  </div>
);

const Panel = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 ${className}`}>
    {children}
  </div>
);

const EmptyState = ({ text = "No calls found for the selected dates." }) => (
  <div className="h-64 flex items-center justify-center text-sm text-gray-500">{text}</div>
);

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */
const EmployeeCallWeeklyReport = () => {
  const navigate = useNavigate();
  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDate(d);
  });

  const [endDate, setEndDate] = useState(() => formatDate(new Date()));
  const [districtFilter, setDistrictFilter] = useState("All Districts");
  const startRef = useRef(null);
  const endRef = useRef(null);

  const [employeesList, setEmployeesList] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [reportData, setReportData] = useState({ summaryData: [], districtData: [] });
  const [loading, setLoading] = useState(false);

  // Retrieve user session
  const loggedInUser = useMemo(() => {
    try {
      const stored =
        sessionStorage.getItem("user") ||
        sessionStorage.getItem("employee") ||
        localStorage.getItem("user") ||
        localStorage.getItem("employee");
      if (stored) return JSON.parse(stored);
    } catch (e) {
      console.error("Error reading session user:", e);
    }
    return null;
  }, []);

  const normalizedRole = useMemo(() => {
    const rawRole = loggedInUser?.member_type || loggedInUser?.role || "";
    return rawRole.trim().toLowerCase();
  }, [loggedInUser]);

  const currentEmpId = useMemo(() => {
    return String(loggedInUser?.employee_id ?? loggedInUser?.id ?? "");
  }, [loggedInUser]);

  const isEmployeeRole = normalizedRole === "employee";
  const isTeamLeadRole =
    normalizedRole === "team lead" ||
    normalizedRole === "teamlead" ||
    normalizedRole === "tl";

  // 1. Fetch dropdown list based on role
  useEffect(() => {
    let cancelled = false;

    const fetchEmployees = async () => {
      try {
        const token = "Bearer sandeep_university_916";

        // Team Lead role: fetch assigned team members
        if (isTeamLeadRole) {
          const res = await fetch(
            "https://sanjivanierp.oakyaitservices.in/api/team_lead_emp_assign/emp_assign_retrieve.php",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
              body: JSON.stringify({
                team_lead_emp_id: currentEmpId,
                start_date: startDate,
                end_date: endDate,
              }),
            }
          );
          if (!res.ok) throw new Error("Failed to load assigned employees");
          const json = await res.json();
          const assigned = Array.isArray(json?.data) ? json.data : [];

          if (!cancelled) {
            setEmployeesList(
              assigned.map((emp) => ({
                id: String(emp.assign_emp_id),
                name: emp.assign_full_name,
              }))
            );
          }
          return;
        }

        // Admin / Manager role: fetch full directory
        if (
          normalizedRole === "admin" ||
          normalizedRole === "manager" ||
          !normalizedRole
        ) {
          const res = await fetch(
            SummaryApi?.getAllEmployees?.url ||
            "https://sanjivanierp.oakyaitservices.in/api/employee/retrieve.php",
            {
              method: SummaryApi?.getAllEmployees?.method || "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
            }
          );
          if (!res.ok) throw new Error("Failed to load employees");
          const json = await res.json();
          const list = Array.isArray(json?.data) ? json.data : [];

          if (!cancelled) {
            setEmployeesList(
              list.map((emp) => ({
                id: String(emp.employee_id),
                name: emp.full_name,
              }))
            );
          }
          return;
        }

        // Employee role: locked to self
        if (isEmployeeRole) {
          if (!cancelled) {
            setEmployeesList([
              {
                id: currentEmpId,
                name: loggedInUser?.full_name || "Self",
              },
            ]);
            setEmployeeFilter(currentEmpId);
          }
        }
      } catch (err) {
        console.error("Error fetching employee dropdown list:", err);
      }
    };

    if (currentEmpId || normalizedRole === "admin" || normalizedRole === "manager") {
      fetchEmployees();
    }

    return () => {
      cancelled = true;
    };
  }, [normalizedRole, isTeamLeadRole, currentEmpId, isEmployeeRole, startDate, endDate, loggedInUser]);

  // 2. Fetch Weekly Report from API
  useEffect(() => {
    let cancelled = false;

    const fetchWeeklyReport = async () => {
      setLoading(true);
      try {
        let endpointUrl = "";
        let endpointMethod = "POST";
        const payload = {
          from_date: startDate,
          to_date: endDate,
        };

        const targetEmployeeId = isEmployeeRole ? currentEmpId : employeeFilter;

        // RULE: If an employee is selected from the dropdown (or role is Employee),
        // ALWAYS use getEmployeesWeeklyReport with employee_id, regardless of role.
        if (targetEmployeeId) {
          endpointUrl =
            SummaryApi?.getEmployeesWeeklyReport?.url ||
            "http://192.168.1.190/multi_university_apis/dashboard/admin_count.php";
          endpointMethod =
            SummaryApi?.getEmployeesWeeklyReport?.method || "POST";
          payload.employee_id = targetEmployeeId;
        } else if (isTeamLeadRole) {
          // Team Lead with "All Team Members" selected
          endpointUrl =
            SummaryApi?.getEmployeesWeeklyReportByTeamLead?.url ||
            "http://192.168.1.190/multi_university_apis/dashboard/team_lead_count.php";
          endpointMethod =
            SummaryApi?.getEmployeesWeeklyReportByTeamLead?.method || "POST";
          payload.team_lead_emp_id = currentEmpId;
        } else {
          // Admin / Manager with "All Employees" selected
          endpointUrl =
            SummaryApi?.getEmployeesWeeklyReport?.url ||
            "http://192.168.1.190/multi_university_apis/dashboard/admin_count.php";
          endpointMethod =
            SummaryApi?.getEmployeesWeeklyReport?.method || "POST";
        }

        const res = await fetch(endpointUrl, {
          method: endpointMethod,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer sandeep_university_916",
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error("Failed to fetch weekly report");
        const json = await res.json();

        if (!cancelled && json?.status) {
          setReportData({
            summaryData: Array.isArray(json?.data) ? json.data : [],
            districtData: Array.isArray(json?.districts) ? json.districts : [],
          });
        }
      } catch (error) {
        console.error("Error fetching employee weekly report:", error);
        if (!cancelled) {
          setReportData({ summaryData: [], districtData: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWeeklyReport();

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, employeeFilter, isEmployeeRole, isTeamLeadRole, currentEmpId]);

  // 3. Aggregate cards, chart data, and district summary
  const { summary, districts } = useMemo(() => {

    const summaryTotals = reportData.summaryData.reduce(
      (acc, item) => ({
        total: acc.total + Number(item.total_calls_count || 0),
        interested: acc.interested + Number(item.total_interested_calls_count || 0),
        mostInterested:
          acc.mostInterested + Number(item.total_most_interested_calls_count || 0),
        notInterested:
          acc.notInterested + Number(item.total_not_interested_calls_count || 0),
        notAnswered:
          acc.notAnswered + Number(item.total_not_answered_calls_count || 0),
        callBack:
          acc.callBack + Number(item?.total_call_back_count || 0)
      }),
      { ...EMPTY_ROW }
    );

    console.log(reportData, 'klkl')



    const districtMap = {};
    reportData.districtData.forEach((row) => {
      const dName = row.district || "Unknown";
      if (!districtMap[dName]) {
        districtMap[dName] = {
          district: dName,
          ...EMPTY_ROW,
        };
      }
      districtMap[dName].total += Number(row.total_calls_count || 0);
      districtMap[dName].interested += Number(row.total_interested_calls_count || 0);
      districtMap[dName].callBack += Number(row.total_call_back_count || 0);
      districtMap[dName].mostInterested += Number(
        row.total_most_interested_calls_count || 0
      );
      districtMap[dName].notInterested += Number(
        row.total_not_interested_calls_count || 0
      );
      districtMap[dName].notAnswered += Number(
        row.total_not_answered_calls_count || 0
      );
    });

    const districtList = Object.values(districtMap);
    return { summary: summaryTotals, districts: districtList };
  }, [reportData]);

  // Reset district filter if chosen district no longer exists in current response
  useEffect(() => {
    if (
      districtFilter !== "All Districts" &&
      !districts.some((d) => d.district === districtFilter)
    ) {
      setDistrictFilter("All Districts");
    }
  }, [districts, districtFilter]);

  const hasData = summary.total > 0;

  const doughnutData = useMemo(
    () => ({
      labels: STATUS.map((s) => s.label),
      datasets: [
        {
          data: STATUS.map((s) => summary[s.key]),
          backgroundColor: STATUS.map((s) => COLORS[s.key]),
          borderColor: "#fff",
          borderWidth: 2,
        },
      ],
    }),
    [summary]
  );

  const visibleDistricts = useMemo(
    () =>
      districtFilter === "All Districts"
        ? districts
        : districts.filter((d) => d.district === districtFilter),
    [districts, districtFilter]
  );

  const barData = useMemo(
    () => ({
      labels: visibleDistricts.map((d) => d.district),
      datasets: STATUS.map((s) => ({
        label: s.label,
        data: visibleDistricts.map((d) => d[s.key]),
        backgroundColor: COLORS[s.key],
        borderRadius: 2,
        barPercentage: 0.85,
        categoryPercentage: 0.7,
      })),
    }),
    [visibleDistricts]
  );

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 18 } },
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#6b7280", font: { size: 12 } } },
      y: {
        beginAtZero: true,
        grace: "12%",
        ticks: { color: "#6b7280", font: { size: 11 }, precision: 0 },
        grid: { color: "#e5e7eb" },
        border: { display: false },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "42%",
    plugins: {
      legend: { display: false },
      centerText: { value: summary?.total?.toLocaleString() },
    },
  };

  const cards = [
    {
      title: "Total Calls",
      value: summary.total,
      note: "Total number of calls made",
      icon: FaPhoneAlt,
      iconBg: "#dbe6fb",
      iconColor: "#2f5fd0",
      cardBg: "#eef3fd",
      border: "#bcd0f5",
    },
    {
      title: "Interested",
      value: summary.interested,
      note: `${pct(summary.interested, summary.total)}% of total calls`,
      icon: FaUser,
      iconBg: "#cdeedf",
      iconColor: COLORS.interested,
      cardBg: "#f1fbf6",
      border: "#c4ead7",
    },
    {
      title: "Most Interested",
      value: summary.mostInterested,
      note: `${pct(summary.mostInterested, summary.total)}% of total calls`,
      icon: FaStar,
      iconBg: "#e2d7f8",
      iconColor: COLORS.mostInterested,
      cardBg: "#f6f2fd",
      border: "#d9cbf3",
    },
    {
      title: "Not Interested",
      value: summary.notInterested,
      note: `${pct(summary.notInterested, summary.total)}% of total calls`,
      icon: FaTimes,
      iconBg: "#fbd3d3",
      iconColor: COLORS.notInterested,
      cardBg: "#fef2f2",
      border: "#f8cccc",
    },
    {
      title: "Not Answered",
      value: summary.notAnswered,
      note: `${pct(summary.notAnswered, summary.total)}% of total calls`,
      icon: FaMinus,
      iconBg: "#fde5c2",
      iconColor: COLORS.notAnswered,
      cardBg: "#fffaf0",
      border: "#f9e0b5",
    },
    {
      title: "Call Back",
      value: summary.callBack,
      note: `${pct(summary.callBack, summary.total)}% of total calls`,
      icon: FaPhoneAlt,
      iconBg: "#fde5c2",
      iconColor: COLORS.callBack,
      cardBg: "#fffaf0",
      border: "#f9e0b5",
    }
  ];

  const thClass = "px-4 py-3 text-left text-sm font-semibold text-gray-600";
  const tdClass = "px-4 py-3 text-sm text-gray-800";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
            <span className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center">
              <FaPhoneAlt size={15} />
            </span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Employee Call Report<span className="text-xl ml-1">(Daily, Weekly)</span>
            </h1>
            <p className="text-sm text-gray-500">
              Overview of total calls and student interest status for the selected period.
            </p>
          </div>
        </div>

        {/* Date range & Employee filter */}
        <div className="inline-flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-2 bg-white text-sm text-gray-800 self-start md:self-auto shadow-sm">
          {/* Employee Dropdown */}
          <div className="relative">
            <select
              value={employeeFilter}
              disabled={isEmployeeRole}
              onChange={(e) => setEmployeeFilter(e.target.value)}
              className={`appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-1.5 text-sm font-medium text-gray-800 bg-white
                focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-[170px] ${isEmployeeRole ? "bg-gray-100 cursor-not-allowed text-gray-600" : ""
                }`}
            >
              {!isEmployeeRole && (
                <option value="">
                  {isTeamLeadRole ? "All Team Members" : "All Employees"}
                </option>
              )}
              {employeesList.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
            {!isEmployeeRole && (
              <FaChevronDown
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                size={10}
              />
            )}
          </div>

          <span className="text-gray-300">|</span>

          <FaRegCalendarAlt className="text-gray-500" />
          <button
            type="button"
            className="relative font-medium"
            onClick={() => startRef.current?.showPicker?.()}
          >
            {fmtDate(startDate)}
            <input
              ref={startRef}
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => e.target.value && setStartDate(e.target.value)}
              className="absolute inset-0 opacity-0 w-full cursor-pointer"
            />
          </button>
          <span className="text-gray-400">→</span>
          <button
            type="button"
            className="relative font-medium"
            onClick={() => endRef.current?.showPicker?.()}
          >
            {fmtDate(endDate)}
            <input
              ref={endRef}
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => e.target.value && setEndDate(e.target.value)}
              className="absolute inset-0 opacity-0 w-full cursor-pointer"
            />
          </button>
          <FaChevronDown className="text-gray-400" size={11} />
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex flex-col items-center justify-center gap-2 text-gray-500">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm">Fetching report data...</span>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 gap-4">
            {cards.map((c) => (
              <StatCard key={c.title} {...c} />
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-5">
            <Panel>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Call Status Distribution</h2>
              {hasData ? (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="w-64 h-64 shrink-0">
                    <Doughnut
                      data={doughnutData}
                      options={doughnutOptions}
                      plugins={[centerTextPlugin, slicePercentPlugin]}
                    />
                  </div>
                  <ul className="flex-1 w-full">
                    {STATUS.map((s, i) => (
                      <li
                        key={s.key}
                        className={`flex items-center py-3 text-sm ${i < STATUS.length - 1 ? "border-b border-gray-100" : ""
                          }`}
                      >
                        <span
                          className="w-3 h-3 rounded-full mr-3"
                          style={{ background: COLORS[s.key] }}
                        />
                        <span className="flex-1 text-gray-700">{s.label}</span>
                        <span className="w-14 text-right font-bold text-gray-900">
                          {summary[s.key]}
                        </span>
                        <span className="w-16 text-right text-gray-600">
                          {pct(summary[s.key], summary.total)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <EmptyState />
              )}
            </Panel>

            <Panel>
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold text-gray-900">Call Status by District</h2>
                <div className="relative">
                  <select
                    value={districtFilter}
                    onChange={(e) => setDistrictFilter(e.target.value)}
                    className="appearance-none border border-gray-200 rounded-md pl-3 pr-8 py-1.5 text-sm font-semibold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    <option>All Districts</option>
                    {districts.map((d) => (
                      <option key={d.district}>{d.district}</option>
                    ))}
                  </select>
                  <FaChevronDown
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                    size={10}
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-2 mb-2">
                {STATUS.map((s) => (
                  <span key={s.key} className="flex items-center text-sm text-gray-600">
                    <span
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ background: COLORS[s.key] }}
                    />
                    {s.label}
                  </span>
                ))}
              </div>

              {hasData ? (
                <div className="h-64">
                  <Bar data={barData} options={barOptions} plugins={[barValuePlugin]} />
                </div>
              ) : (
                <EmptyState />
              )}
            </Panel>
          </div>

          {/* District table */}
          <Panel>
            <h2 className="text-lg font-bold text-gray-900 mb-3">District Wise Call Summary</h2>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={thClass}>S.No</th>
                    <th className={thClass}>District</th>
                    <th className={thClass}>Total Calls</th>
                    <th className={thClass}>Interested</th>
                    <th className={thClass}>Most Interested</th>
                    <th className={thClass}>Not Interested</th>
                    <th className={thClass}>Call Back</th>
                    <th className={thClass}>Not Answered</th>
                    <th className={thClass}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {districts.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                        No calls found for the selected dates.
                      </td>
                    </tr>
                  ) : (
                    districts.map((d, i) => (
                      <tr key={d.district} className="hover:bg-gray-50">
                        <td className={tdClass}>{i + 1}</td>
                        <td className={tdClass}>{d.district}</td>
                        <td className={tdClass}>{d.total}</td>
                        <td className={tdClass}>{d.interested}</td>
                        <td className={tdClass}>{d.mostInterested}</td>
                        <td className={tdClass}>{d.notInterested}</td>
                        <td className={tdClass}>{d.callBack}</td>
                        <td className={tdClass}>{d.notAnswered}</td>
                        <td className={tdClass}>
                          <button
                            type="button"
                            onClick={() =>
                              navigate("/employee-calllogs", {
                                state: {
                                  district: d.district,
                                  startDate,
                                  endDate,
                                  employeeId: employeeFilter || (isEmployeeRole ? currentEmpId : ""),
                                },
                              })
                            }
                            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1 rounded transition-colors"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
};

export default EmployeeCallWeeklyReport;